import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Lightbulb, Loader2, Plus, RefreshCw, Trash2, X, Crosshair } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth, isAdmin } from "../contexts/AuthContext";
import { ProfileBars, TransitionCompare } from "./ShotProfileGraphic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });

const fmtPct = (v) => (v === null || v === undefined ? "—" : `${Math.round(Number(v))}%`);
const fmtPpp = (v) => {
  if (v === null || v === undefined) return "—";
  const s = Number(v).toFixed(2);
  return s.startsWith("0") ? s.slice(1) : s;
};
const fmtRate = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(2));

const TRANSITION_ROWS = [
  { label: "Transition" },
  { label: "Half Court" },
];
const NUM_INPUT = "w-full bg-[#0f0f0f] border border-white/10 px-2 py-1.5 text-sm text-white text-center focus:outline-none focus:border-[var(--org-accent)]";

export function profileBars(kind, rows) {
  return rows.map((r, i) => ({
    key: i,
    label: r.label,
    left: fmtPct(r.use_pct),
    right: fmtPpp(r.ppp),
    leftRank: r.use_rank,
    rightRank: r.ppp_rank,
    sub: kind === "transition" ? [
      { label: "2FG%", value: fmtPct(r.fg2_pct) },
      { label: "3FG%", value: fmtPct(r.fg3_pct) },
      { label: "FT RATE", value: fmtRate(r.ft_rate) },
    ] : null,
  }));
}

function emptyRows(kind) {
  if (kind === "transition") return TRANSITION_ROWS.map((r) => ({ ...r, use_pct: "", ppp: "", fg2_pct: "", fg3_pct: "", ft_rate: "" }));
  return Array.from({ length: 4 }, () => ({ label: "", use_pct: "", ppp: "" }));
}

function NumCell({ value, onChange, tid, placeholder }) {
  return <input type="number" step="0.01" value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={NUM_INPUT} data-testid={tid} />;
}

function ProfileForm({ kind, onCancel, onSaved, playerId }) {
  const [label, setLabel] = useState("");
  const [rows, setRows] = useState(emptyRows(kind));
  const [saving, setSaving] = useState(false);
  const isTr = kind === "transition";
  const setCell = (i, f, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [f]: v } : r)));
  const save = async () => {
    const filled = rows.filter((r) => r.label.trim());
    if (filled.length === 0) return toast.error("Enter at least one row");
    setSaving(true);
    try {
      const r = await client.post(`/players/${playerId}/shot-profiles`, { kind, label, rows: filled });
      onSaved(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  const cols = isTr ? "grid-cols-[1.4fr_1fr_1fr_1fr_1fr_1fr]" : "grid-cols-[1.6fr_1fr_1fr_auto]";
  return (
    <div className="border border-[var(--org-accent)]/50 bg-[#141414] p-4 mb-4" data-testid={`sp-${kind}-form`}>
      <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g., Through 20 games)"
        className="w-full bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)] mb-3"
        data-testid={`sp-${kind}-label`} />
      <div className={`grid ${cols} gap-2 text-[9px] uppercase tracking-[0.2em] text-[#757575] mb-1 px-1`}>
        <div>{isTr ? "Situation" : "Play Type"}</div><div className="text-center">Use %</div><div className="text-center">PPP</div>
        {isTr && <><div className="text-center">2FG%</div><div className="text-center">3FG%</div><div className="text-center">FT Rate</div></>}
        {!isTr && <div />}
      </div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className={`grid ${cols} gap-2 items-center`}>
            {isTr ? (
              <div className="text-[11px] uppercase tracking-[0.2em] text-white px-1">{r.label}</div>
            ) : (
              <input value={r.label} onChange={(e) => setCell(i, "label", e.target.value)} placeholder={`Play type ${i + 1}`}
                className="bg-[#0f0f0f] border border-white/10 px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
                data-testid={`sp-${kind}-row-${i}-label`} />
            )}
            <NumCell value={r.use_pct} onChange={(v) => setCell(i, "use_pct", v)} tid={`sp-${kind}-row-${i}-use_pct`} placeholder="27" />
            <NumCell value={r.ppp} onChange={(v) => setCell(i, "ppp", v)} tid={`sp-${kind}-row-${i}-ppp`} placeholder="1.08" />
            {isTr && <>
              <NumCell value={r.fg2_pct} onChange={(v) => setCell(i, "fg2_pct", v)} tid={`sp-${kind}-row-${i}-fg2_pct`} placeholder="52" />
              <NumCell value={r.fg3_pct} onChange={(v) => setCell(i, "fg3_pct", v)} tid={`sp-${kind}-row-${i}-fg3_pct`} placeholder="36" />
              <NumCell value={r.ft_rate} onChange={(v) => setCell(i, "ft_rate", v)} tid={`sp-${kind}-row-${i}-ft_rate`} placeholder=".30" />
            </>}
            {!isTr && (
              <button onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} disabled={rows.length <= 1} className="text-[#757575] hover:text-red-300 disabled:opacity-20 px-1" data-testid={`sp-${kind}-row-${i}-remove`}><X className="w-3.5 h-3.5" /></button>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4">
        {!isTr ? (
          <button onClick={() => setRows((rs) => [...rs, { label: "", use_pct: "", ppp: "" }])} disabled={rows.length >= 6}
            className="text-[10px] uppercase tracking-[0.2em] text-[var(--org-accent)] hover:underline disabled:opacity-30" data-testid={`sp-${kind}-add-row`}>+ Add play type</button>
        ) : <div />}
        <div className="flex gap-2">
          <button onClick={onCancel} className="px-3 py-1.5 border border-white/20 text-[#a0a0a0] text-[10px] uppercase tracking-[0.3em] font-bold hover:text-white" data-testid={`sp-${kind}-cancel`}>Cancel</button>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-1.5 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)] disabled:opacity-60" data-testid={`sp-${kind}-save`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null} {saving ? "Analyzing…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ProfileRecs({ narrative, rows, light = false, testId = "sp-recs" }) {
  const nar = narrative || {};
  const growth = nar.headline_growth || [];
  const strengths = nar.headline_strengths || [];
  const recs = nar.recommendations || {};
  const hasAny = growth.length || strengths.length || Object.keys(recs).length;
  if (!hasAny) return null;
  // Pick-and-roll flagged as a growth area → point the player at the Ball Screen menu.
  const isPnR = (label) => /\b(pnr|p&r|pick.?and.?roll|pick.?&.?roll|ball.?screen)\b/i.test(label || "");
  const pnrFlagged = !light && (growth.some((g) => isPnR(g.label)) || (rows || []).some((r) => isPnR(r.label) && recs[r.label]));
  const c = light
    ? { text: "#111827", muted: "#374151", growth: "#b91c1c", strength: "#065f46", accent: "#8a7020", border: "#d8b854", box: "#f9fafb", boxBorder: "#e5e7eb" }
    : { text: "#ffffff", muted: "#d8d8d8", growth: "#f87171", strength: "#4ade80", accent: "var(--org-accent)", border: "rgba(216,184,84,0.4)", box: "rgba(216,184,84,0.05)", boxBorder: "rgba(216,184,84,0.4)" };
  const fs = light ? 12 : 13;
  return (
    <div data-testid={testId} style={{ fontFamily: light ? "Helvetica, Arial, sans-serif" : undefined }}>
      {(growth.length > 0 || strengths.length > 0) && (
        <div style={{ border: `1px solid ${c.boxBorder}`, background: c.box, padding: 14, marginBottom: 12 }}>
          {growth.length > 0 && (
            <div style={{ marginBottom: strengths.length ? 10 : 0 }}>
              <div style={{ fontSize: 10, letterSpacing: 2, color: c.growth, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Top Growth Areas</div>
              {growth.map((g, i) => <div key={i} style={{ fontSize: fs, color: c.text, lineHeight: 1.5, marginBottom: 4 }}><strong>{g.label}:</strong> {g.sentence}</div>)}
            </div>
          )}
          {strengths.length > 0 && (
            <div>
              <div style={{ fontSize: 10, letterSpacing: 2, color: c.strength, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Strengths to Lean Into</div>
              {strengths.map((s, i) => <div key={i} style={{ fontSize: fs, color: c.text, lineHeight: 1.5, marginBottom: 4 }}><strong>{s.label}:</strong> {s.sentence}</div>)}
            </div>
          )}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {(rows || []).filter((r) => recs[r.label]).map((r) => (
          <div key={r.label} data-testid={`${testId}-${r.label}`} style={{ borderLeft: `3px solid ${c.border}`, padding: "2px 10px", fontSize: fs, color: c.muted, lineHeight: 1.5 }}>
            <span style={{ fontSize: 9, letterSpacing: 2, color: c.accent, fontWeight: 700, textTransform: "uppercase", marginRight: 6 }}>{r.label}</span>
            {recs[r.label]}
          </div>
        ))}
      </div>
      {pnrFlagged && (
        <Link to="/tactical-iq/ball-screen" className="mt-3 inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-[#3aa0ff]/50 text-[#3aa0ff] hover:bg-[#3aa0ff]/10" style={{ transition: "background-color .15s" }} data-testid="sp-ball-screen-link">
          <Crosshair className="w-3.5 h-3.5" /> Study the Ball Screen menu →
        </Link>
      )}
    </div>
  );
}

export function ShotProfileCard({ playerId, kind, title, description, onSelectedChange }) {
  const { user } = useAuth();
  const canEdit = isAdmin(user);
  const [profiles, setProfiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [regen, setRegen] = useState(false);
  const [recsOpen, setRecsOpen] = useState(false);

  const regenerate = async () => {
    if (!selected) return;
    setRegen(true);
    try {
      const r = await client.post(`/players/${playerId}/shot-profiles/${selected.id}/narrative`);
      setProfiles((list) => list.map((p) => (p.id === r.data.id ? r.data : p)));
      setSelected(r.data);
      toast.success("Recommendations ready");
    } catch (e) { toast.error("Generation failed"); }
    finally { setRegen(false); }
  };

  useEffect(() => { onSelectedChange?.(selected); }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true;
    setLoading(true);
    client.get(`/players/${playerId}/shot-profiles`, { params: { kind } })
      .then((r) => { if (!alive) return; const list = r.data.profiles || []; setProfiles(list); setSelected(list[0] || null); })
      .catch(() => toast.error("Load failed"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [playerId, kind]);

  const onSaved = (doc) => { setProfiles((p) => [doc, ...p]); setSelected(doc); setShowForm(false); };
  const remove = async (p) => {
    if (!window.confirm("Delete this snapshot?")) return;
    try {
      await client.delete(`/players/${playerId}/shot-profiles/${p.id}`);
      setProfiles((list) => { const next = list.filter((x) => x.id !== p.id); setSelected((s) => (s?.id === p.id ? next[0] || null : s)); return next; });
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="border border-white/10 bg-[#141414] p-5" data-testid={`sp-${kind}-card`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[var(--org-accent)]">{title}</div>
          <div className="text-[11px] text-[#757575] mt-1">{description}</div>
        </div>
        {canEdit && !showForm && (
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-3 py-1.5 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)] shrink-0" data-testid={`sp-${kind}-new-btn`}>
            <Plus className="w-3 h-3" /> New
          </button>
        )}
      </div>
      {showForm && <ProfileForm kind={kind} playerId={playerId} onCancel={() => setShowForm(false)} onSaved={onSaved} />}
      {profiles.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid={`sp-${kind}-history`}>
          {profiles.map((p) => (
            <div key={p.id} onClick={() => setSelected(p)} data-testid={`sp-${kind}-history-${p.id}`}
              className={`group flex items-center gap-2 border px-2.5 py-1 cursor-pointer text-[10px] ${selected?.id === p.id ? "border-[var(--org-accent)] text-white" : "border-white/10 text-[#a0a0a0] hover:text-white"}`}>
              <span>{p.label || "Snapshot"}</span>
              <span className="font-mono text-[#757575]">{(p.created_at || "").slice(0, 10)}</span>
              {canEdit && <button onClick={(e) => { e.stopPropagation(); remove(p); }} className="opacity-0 group-hover:opacity-100 text-[#757575] hover:text-red-300"><Trash2 className="w-3 h-3" /></button>}
            </div>
          ))}
        </div>
      )}
      {loading && <div className="text-[#757575] text-xs flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> loading…</div>}
      {!loading && selected && (kind === "transition"
        ? <TransitionCompare rows={selected.rows || []} testId={`sp-${kind}-graphic`} />
        : <ProfileBars rows={profileBars(kind, selected.rows || [])} title={title} testId={`sp-${kind}-graphic`} />)}
      {!loading && selected && (
        <div className="mt-6 border-t border-white/10 pt-4">
          <div className="flex items-center justify-between mb-1">
            <button onClick={() => setRecsOpen((o) => !o)} className="flex items-center gap-2 text-left group" data-testid={`sp-${kind}-recs-toggle`} aria-expanded={recsOpen}>
              <ChevronDown className={`w-3.5 h-3.5 text-[var(--org-accent)] transition-transform duration-200 ${recsOpen ? "" : "-rotate-90"}`} />
              <Lightbulb className="w-3.5 h-3.5 text-[var(--org-accent)]" />
              <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[var(--org-accent)] group-hover:text-white transition-colors">Coach Recommendations</div>
            </button>
            {canEdit && (
              <button onClick={regenerate} disabled={regen} className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.25em] text-[#a0a0a0] hover:text-white disabled:opacity-50" data-testid={`sp-${kind}-regen`}>
                {regen ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} {selected.narrative?.recommendations && Object.keys(selected.narrative.recommendations).length ? "Regenerate" : "Generate"}
              </button>
            )}
          </div>
          {recsOpen && (
            <div className="mt-3" data-testid={`sp-${kind}-recs-body`}>
              {selected.narrative?.recommendations && Object.keys(selected.narrative.recommendations).length
                ? <ProfileRecs narrative={selected.narrative} rows={selected.rows} testId={`sp-${kind}-recs`} />
                : <div className="text-[11px] text-[#757575]">{regen ? "Coach Dags is reviewing the numbers…" : "No recommendations yet."}</div>}
            </div>
          )}
        </div>
      )}
      {!loading && !selected && !showForm && (
        <div className="border border-dashed border-white/10 py-10 text-center text-[#757575] text-xs">
          {canEdit ? "No data yet — click New to enter numbers." : "No data yet."}
        </div>
      )}
    </div>
  );
}
